import type { ReferenceBundle } from '../domain/types'

// Cache local do catálogo (reference), usado quando o backend falha no boot.
//
// A chave carrega uma versão: catálogo gravado por um build antigo nunca é aplicado
// como se fosse do formato atual. Antes o JSON do cache ia direto para o state com um
// cast para ReferenceBundle, então um payload fora do contrato (ex.: sem
// `serviceConfigs`) estourava no primeiro `reference.serviceConfigs.map` e derrubava o
// app inteiro no ErrorBoundary global.
const CACHE_VERSION = 'v2'

// Menor superfície que o módulo precisa do Web Storage: permite injetar um dublê nos
// testes (o jsdom deste projeto não expõe localStorage) e aceitar não ter storage.
export interface ReferenceCacheStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

const LIST_FIELDS = [
  'services',
  'serviceConfigs',
  'sessions',
  'offerings',
  'customerFields',
  'journeyFields',
  'availableDays',
] as const

export function referenceCacheKey(providerId: string, locationId: string): string {
  return `fz-reference:${CACHE_VERSION}:${providerId}-${locationId}`
}

function defaultStorage(): ReferenceCacheStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    // Acessar localStorage pode lançar (modo privado / storage desabilitado).
    return null
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function parseCachedReference(raw: string | null): ReferenceBundle | null {
  if (!raw) { return null }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }

  if (!isRecord(parsed)) { return null }
  for (const field of LIST_FIELDS) {
    if (!Array.isArray(parsed[field])) { return null }
  }
  if (!isRecord(parsed.presets)) { return null }

  return parsed as unknown as ReferenceBundle
}

export function readCachedReference(
  providerId: string,
  locationId: string,
  storage: ReferenceCacheStorage | null = defaultStorage(),
): ReferenceBundle | null {
  if (!storage) { return null }
  try {
    return parseCachedReference(storage.getItem(referenceCacheKey(providerId, locationId)))
  } catch {
    return null
  }
}

export function writeCachedReference(
  providerId: string,
  locationId: string,
  bundle: ReferenceBundle,
  storage: ReferenceCacheStorage | null = defaultStorage(),
): void {
  if (!storage) { return }
  try {
    storage.setItem(referenceCacheKey(providerId, locationId), JSON.stringify(bundle))
  } catch { /* cache é otimização: falhar aqui não pode atrapalhar o boot */ }
}
